import { CalendarPlanActivity, CalendarPlanTheme, CalendarPlanThemePeriod, Lesson } from '@yandex-int/k-common';
import { CONTENT_TYPE } from '@yandex-int/k-common/typings/api/models/content';
import b from 'b_';
import { SimpleCallback } from 'platform-components';
import { getFeatureFlags } from 'platform-components/src/components/app-factory/selectors';
import { sortBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import lessonActions from 'common.components/lesson/actions';
import { MarkdownExtXContent } from 'common.components/markdown/markdown';

import { CalendarPlanShowcaseCarouselHeader } from '../../calendar-plan-showcase/__carousel-header/calendar-plan-showcase__carousel-header';
import { CalendarPlanShowcaseCarousel } from '../../calendar-plan-showcase/__carousel/calendar-plan-showcase__carousel';
import { CalendarPlanShowcaseLessonThemeName } from '../../calendar-plan-showcase/__lesson-theme-name/calendar-plan-showcase__lesson-theme-name';
import { MetrikaClickFromParam, useMetrika } from '../../library.calendar-plan-showcase/hooks/useMetrika';
import { useLessonDetailsQuery } from '../../library.calendar-plan.new/queries';
import { getLessonActivity, mapToCalendarPlanShowcaseCard } from '../utils';

import './calendar-plan-theme__content.scss';

interface Props {
    currentTheme: {
        theme: CalendarPlanTheme;
        period: CalendarPlanThemePeriod;
        activities: CalendarPlanActivity[];
    };
    currentThemeLesson?: Lesson;
    nextTheme?: {
        theme: CalendarPlanTheme;
        period: CalendarPlanThemePeriod;
        activities: CalendarPlanActivity[];
    };
    nextThemeLesson?: Lesson;
    onNextItemClick: SimpleCallback;
    onPrevItemClick: SimpleCallback;
    onTeaserOpenPlanButtonClick: SimpleCallback;
    hasPrevItem: boolean;
    shouldShowSkeleton?: boolean;
}

export const CalendarPlanThemeContent = (props: Props) => {
    const {
        currentTheme,
        currentThemeLesson,
        nextTheme,
        nextThemeLesson,
        hasPrevItem,
        onNextItemClick,
        onPrevItemClick,
        onTeaserOpenPlanButtonClick,
    } = props;
    const isTitleAndDescriptionFromCmsEnabled =
        useSelector(getFeatureFlags)?.calendarPlanItemTitleAndDescriptionFromCmsEnabled;
    const lessonId = currentThemeLesson?.id;
    const nextThemeLessonActivity = getLessonActivity(nextTheme?.activities || []);
    const shouldShowNextLabel = Boolean(nextTheme);

    const params = useParams();
    const dispatch = useDispatch();
    const intl = useIntl();
    const courseId = useSelector(getCurrentYearCourseId);
    const lessonDetailsQuery = useLessonDetailsQuery(courseId, lessonId);

    const showcaseNextItemCard = nextThemeLessonActivity?.additional_info
        ? mapToCalendarPlanShowcaseCard({
              period: nextTheme!.period,
              lessonViewSettings: nextThemeLessonActivity.additional_info,
              lesson: nextThemeLesson || null,
          })
        : undefined;
    const { reachLessonGiveButtonClick, reachUploadCalendarButtonClick } = useMetrika(
        new Date(currentTheme.period.start_date)
    );

    const handleLessonCardPreviewClick = useCallback(() => {
        if (lessonId) {
            dispatch(lessonActions.clearEditor());
            dispatch(
                lessonViewActions.loadLessonViewData({
                    lessonId,
                    courseId,
                })
            );
            dispatch(lessonViewActions.toggleModal(true));
            reachLessonGiveButtonClick(lessonId);
        }
    }, [courseId, dispatch, lessonId, reachLessonGiveButtonClick]);

    const handleTeaserUploadPlanButtonClick = useCallback(() => {
        const uploadCalendarUrl = intl.formatMessage(
            { id: 'calendarPlan.uploadUrl', defaultMessage: '' },
            { ...params }
        );

        reachUploadCalendarButtonClick(MetrikaClickFromParam.UNAVAILABLE_HGZ);

        window.open(uploadCalendarUrl, '_blank');
    }, [intl, params, reachUploadCalendarButtonClick]);

    const { orderedCurrentThemeActivities, currentThemeLessonActivity } = useMemo(() => {
        const orderedActivities = sortBy(
            currentTheme.activities,
            (activity) => activity.additional_info?.order ?? Infinity
        );
        const lessonActivity = getLessonActivity(orderedActivities);
        return {
            orderedCurrentThemeActivities: orderedActivities,
            currentThemeLessonActivity: lessonActivity,
        };
    }, [currentTheme.activities]);

    return (
        <>
            <CalendarPlanShowcaseCarouselHeader
                mix={b('library-calendar-plan-showcase', 'carousel-header')}
                onNextLabelClick={onNextItemClick}
                showNextLabel={shouldShowNextLabel}
            >
                <CalendarPlanShowcaseLessonThemeName name={currentTheme.theme.description} />
            </CalendarPlanShowcaseCarouselHeader>
            {currentThemeLessonActivity?.additional_info && (
                <CalendarPlanShowcaseCarousel
                    card={mapToCalendarPlanShowcaseCard({
                        period: currentTheme.period,
                        lessonViewSettings: currentThemeLessonActivity.additional_info,
                        lesson: currentThemeLesson || null,
                        lessonDetails: lessonDetailsQuery.data || null,
                    })}
                    hasPrevCard={hasPrevItem}
                    lessonTeaserConfig={{}}
                    nextCard={showcaseNextItemCard}
                    onLessonClick={handleLessonCardPreviewClick}
                    onNextCardClick={onNextItemClick}
                    onPrevCardClick={onPrevItemClick}
                    onTeaserOpenPlanButtonClick={onTeaserOpenPlanButtonClick}
                    onTeaserUploadPlanButtonClick={handleTeaserUploadPlanButtonClick}
                />
            )}
            <div className={b('library-calendar-plan-showcase', 'lesson-additional-content')}>
                {orderedCurrentThemeActivities.map((activity) => {
                    switch (activity.content_type) {
                        case CONTENT_TYPE.MARKDOWN: {
                            const content = activity.additional_info?.content;

                            if (content) {
                                return (
                                    <div
                                        className={b(
                                            'library-calendar-plan-showcase',
                                            'lesson-additional-content-item'
                                        )}
                                        key={activity.id}
                                    >
                                        {isTitleAndDescriptionFromCmsEnabled && (
                                            <div className="lesson-additional-content-item__info">
                                                {activity.additional_info?.title && (
                                                    <span className="lesson-additional-content-item__title">
                                                        {activity.additional_info?.title}
                                                    </span>
                                                )}
                                                {activity.additional_info?.description && (
                                                    <span className="lesson-additional-content-item__description">
                                                        {activity.additional_info?.description}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <MarkdownExtXContent>{content}</MarkdownExtXContent>
                                    </div>
                                );
                            }
                            return null;
                        }

                        default:
                            return null;
                    }
                })}
            </div>
        </>
    );
};
